/**
 * Voice → structured answers (pure): map "yes" / "the second one" / free speech onto a question's
 * options, and find the agent's reply to a spoken discussion message in the item's transcript.
 */
import type { QuestionItem } from '@viko/protocol'
import type { TimelineEntry } from '@viko/transcript-ui'

const norm = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
const YES = /^(yes|yeah|yep|sure|ok|okay|do it|go ahead|approve|confirm)$/
const NO = /^(no|nope|don'?t|do not|skip|decline)$/
const PICK: Record<string, number> = {
  'first one': 0,
  first: 0,
  'option one': 0,
  'the first': 0,
  'second one': 1,
  second: 1,
  'option two': 1,
  'third one': 2,
  third: 2,
  'option three': 2,
  'last one': -1,
  last: -1,
}

/**
 * The labels to send for one question, or undefined when the spoken answer fits no option and
 * free text is not allowed (voice mode then reads the options back instead of guessing).
 */
export function answerFor(
  q: Pick<QuestionItem, 'options' | 'allowOther' | 'multiSelect'>,
  spoken: string,
): string[] | undefined {
  const n = norm(spoken)
  if (!n) return undefined
  const labels = q.options.map((o) => o.label)
  const exact = labels.find((l) => norm(l) === n)
  if (exact) return [exact]
  const pick = PICK[n.replace(/^the /, '')] ?? PICK[n]
  if (pick !== undefined && labels.length) {
    const l = pick === -1 ? labels.at(-1) : labels[pick]
    if (l) return [l]
  }
  if (YES.test(n)) {
    const y = labels.find((l) => /^(yes|approve|confirm|ok)\b/i.test(l.trim()))
    if (y) return [y]
  }
  if (NO.test(n)) {
    const x = labels.find((l) => /^(no|skip|decline|cancel)\b/i.test(l.trim()))
    if (x) return [x]
  }
  const starts = labels.filter((l) => norm(l).startsWith(n) || n.startsWith(norm(l)))
  if (starts.length === 1) return starts
  if (q.allowOther !== false) return [spoken.trim()]
  return undefined
}

/**
 * The agent's reply to a message sent when the item's transcript had `baseline` turns: the text
 * so far and whether it finished. Undefined until the reply's turn appears.
 */
export function replyAfter(
  entries: readonly TimelineEntry[],
  baseline: number,
): { text: string; done: boolean } | undefined {
  const turns = entries.filter(
    (e): e is Extract<TimelineEntry, { kind: 'turn' }> => e.kind === 'turn',
  )
  if (turns.length <= baseline) return undefined
  const t = turns[turns.length - 1]!.turn
  const text = t.items
    .flatMap((i) => (i.type === 'text' ? [(i as { text: string }).text] : []))
    .join('\n\n')
  return { text, done: t.state !== 'streaming' }
}

export const turnCount = (entries: readonly TimelineEntry[]) =>
  entries.filter((e) => e.kind === 'turn').length
